import dayjs from "dayjs";
import {
  AuditLogsDataType,
  FormatValueForCSV,
  RecordDataType,
  RequiredColumn,
  RiskTypeCount,
} from "../interface";
import { DateRange } from "../type/enums";
import { requiredAuditLogColumns, requiredColumns } from "./constants";
import { ENDPOINT } from "../api/endpoint";
import { API_BASE_URL } from "../config";
import { expandCountryFilters } from "../constants/countryGroups";
import { expandFieldLabelFilters } from "../constants/fieldLabelAggregations";

/**
 * It is used to get the total evaluation count based on the risk type
 * @param evaluationType
 * @returns
 */
export const getTotalEvaluationCount = (
  evaluationType: RiskTypeCount | null,
) => {
  if (!evaluationType || Object.keys(evaluationType).length === 0) return "-";
  return String(
    Object.values(evaluationType).reduce(
      (acc: number, val: number) => acc + val,
      0,
    ),
  );
};

/**
 * Method to convert the date string into a formatted date
 *
 * @param dateInput
 * @param format // Default value is DD MMM YYYY
 * @returns
 */
export const getFormattedDate = (
  dateInput: string | Date | null | undefined,
  format: string = "DD MMM YYYY",
) => {
  if (!dateInput || dateInput === "-") return "-";
  const date = dayjs(dateInput);
  return date.isValid() ? date.format(format) : "-";
};

/**
 * Method to convert the date string into a formatted date
 *
 * @param dateInput
 * @param format // Default value is DD MMM YYYY
 * @returns
 */
export const getFormattedCreateDate = (
  dateInput: string | Date,
  format: string = "DD MMM YYYY HH:mm:ss",
) => {
  const isDateInvalid = dateInput === "None";
  const date = dayjs(dateInput);
  const formattedDate =
    isDateInvalid || !date.isValid() ? "None" : date.format(format);
  return formattedDate;
};

/**
 * Generates a style object for an avatar based on the input name.
 * The avatar includes a background color derived from the name
 * and initials extracted from the first and last names.
 *
 * @param {string} name - The input name to generate the avatar from.
 * @returns {object} - An object containing styling and children properties for the avatar.
 */
export const stringAvatar = (name: string) => {
  const splitName = name.split(" ");
  return {
    sx: {
      bgcolor: "#D7E0F7",
      color: "#0B41CD",
    },
    children:
      splitName.length > 1
        ? `${splitName[0][0]}${splitName[1][0]}`
        : splitName[0][0],
  };
};

/**
 * Gets session data of a user from the sessionStorage
 *
 * @returns {object} - An object containing user session data.
 */
export const getSessionData = () => {
  const isLocalDevelopment = process.env.REACT_APP_LOCAL_DEVELOPMENT === "true";

  // Return mock data in local development mode
  if (isLocalDevelopment) {
    return {
      firstName: "Developer",
      lastName: "User",
      userId: "dev-user",
      email: "developer@local.dev",
      envGroups: ["dev", "tst", "prd"],
      role: "admin",
      profiles: ["PV", "PCO", "PTQ"],
      sessionIndex: "mock-session-index",
      authInstance: new Date().toISOString(),
    };
  }

  const sessionKey = "sessionData";
  const sessionData = sessionStorage.getItem(sessionKey);
  let userInfo = null;
  if (sessionData) {
    userInfo = JSON.parse(sessionData);
  }
  return userInfo;
};

export const revokeServerSession = () => {
  if (process.env.REACT_APP_LOCAL_DEVELOPMENT === "true") return;

  try {
    const userInfo = getSessionData();
    if (!userInfo?.sessionIndex) return;

    fetch(`${API_BASE_URL}${ENDPOINT.LOGOUT}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.sessionIndex}`,
        "X-Authn-Instant": userInfo.authInstance,
        "X-Userid": userInfo.userId,
      },
    }).catch(() => undefined);
  } catch {
    return;
  }
};

/**
 *  Mapper function for records - this maps the record data from API model type to UI model.
 *
 * @param data records array
 * @returns format records array with UI data model
 */
// eslint-disable-next-line complexity
export const formatRecordData = (data: any): RecordDataType[] => {
  if (!data || data.length === 0) return [];
  if (data?.message !== undefined) return [];

  // eslint-disable-next-line complexity
  return data?.map((item: any) => ({
    sourceId: item.source_system,
    recordId: item.ts_id,
    reviewType: item.review_types || "-",
    // Prefer the source date; keep the tracker date as the historical fallback.
    modified: item.source_modified_at || item.last_modified_date,
    extracted: item.created_dt,
    region: item.ts_region,
    country: item.country,
    product: item.product || "-",
    ocepLink: item.ocep_link,
    risk: item.risk_types || "-",
    hcSeverity: item.hc_severity,
    aePcSsSeverity: item.ae_pc_ss_severity,
    hcReason: item.hc_reason,
    aePcSsReason: item.ae_pc_ss_reason,
    severity: item.hc_severity || item.ae_pc_ss_severity || "",

    status: item.status || "-",
    affiliates: item.affiliates,
    creatorEmail: item.sample_creator_email,
    creatorEmails: item.creator_emails || [],
    responsibleEmails: item.responsible_emails || item.creator_emails || [],
    ocepIds: item.ocep_ids || [],
    sampleId: item.sample_id,
    isSampled: item.sample,
    pass: item.pass_count
      ? item.pass_count.reduce(
          (total: number, current: number) => (total += current),
          0,
        )
      : 0,
    fail: item.fail_count
      ? item.fail_count.reduce(
          (total: number, current: number) => (total += current),
          0,
        )
      : 0,
    evaluation: evaluateRisk(
      item.evaluation_risk_type,
      item.fail_count,
      item.pass_count,
    ),
    humanRisks: item.evaluation_risk_type,
    sampleReviewType: item.sample_review_type,
    autoReviewed: item.auto_reviewed,
  }));
};

/**
 * Filters an array of strings based on a case-insensitive search term.
 *
 * @param {string[]} filterArray - The array of strings to filter.
 * @param {string} searchFilter - The search string to match against.
 * @returns {string[]} A filtered array containing strings that include the search term.
 */
export const filteredArray = (filterArray: string[], searchFilter: string) => {
  return (
    filterArray?.filter((item: string) =>
      item?.toLowerCase().includes(searchFilter.toLowerCase()),
    ) || []
  );
};

/**
 * Returns the start and end date strings (YYYY-MM-DD) based on the selected date range type.
 *
 * @param {DateRange} value - The enum value representing the selected date range.
 * @returns {[string, string]} A tuple containing start and end date strings. Returns empty strings for custom or unknown ranges.
 */
export const getRangeDate = (value: DateRange) => {
  const today = dayjs();
  let start = "";
  let end = "";

  switch (value) {
    case DateRange.YESTERDAY:
      start = today.subtract(1, "day").format("YYYY-MM-DD");
      end = start;
      break;
    case DateRange.LAST_7_DAYS:
      start = today.subtract(7, "days").format("YYYY-MM-DD");
      end = today.format("YYYY-MM-DD");
      break;
    case DateRange.LAST_14_DAYS:
      start = today.subtract(14, "days").format("YYYY-MM-DD");
      end = today.format("YYYY-MM-DD");
      break;
    case DateRange.THIS_MONTH:
      start = today.startOf("month").format("YYYY-MM-DD");
      end = today.format("YYYY-MM-DD");
      break;
    case DateRange.PREVIOUS_MONTH:
      start = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      end = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case DateRange.CUSTOM:
      start = "";
      end = "";
      break;
    default:
      break;
  }
  return [start, end];
};

/**
 * Evaluates the distribution of risk types (AE, HC, SS) across three categories:
 * pass, fail, and needsEvaluation, based on corresponding risk type labels and counts.
 *
 * @param {any[]} evaluation_risk_type - Array of risk type labels (e.g., 'AE', 'HC', 'SS') for each index.
 * @param {number[] | null} [fail=null] - Array of failure counts corresponding to risk types.
 * @param {number[] | null} [pass=null] - Array of pass counts corresponding to risk types.
 * @param {number[] | null} [needsEvaluation=null] - Array of "needs evaluation" counts corresponding to risk types.
 * @returns {{
 *   pass: { AE: number; HC: number; SS: number };
 *   fail: { AE: number; HC: number; SS: number };
 *   needsEvaluation: { AE: number; HC: number; SS: number };
 * }} An object with categorized risk counts.
 */
export function evaluateRisk(
  evaluation_risk_type: any,
  fail: number[] | null = null,
  pass: number[] | null = null,
  needsEvaluation: number[] | null = null,
) {
  // Initialize the result object
  const evaluation = {
    pass: { AE: 0, HC: 0, SS: 0, PC: 0 },
    fail: { AE: 0, HC: 0, SS: 0, PC: 0 },
    needsEvaluation: { AE: 0, HC: 0, SS: 0, PC: 0 },
  };

  // Helper function to update counts
  function updateCount(
    category: "fail" | "pass" | "needsEvaluation",
    counts: number[] | null,
  ) {
    if (counts) {
      evaluation[category] = evaluation_risk_type.reduce(
        (acc: any, type: any, index: number) => {
          acc[type] = counts[index] || 0;
          return acc;
        },
        { AE: 0, HC: 0, SS: 0, PC: 0 },
      );
    }
  }

  // Update each category
  updateCount("fail", fail);
  updateCount("pass", pass);
  updateCount("needsEvaluation", needsEvaluation);
  return evaluation;
}

/**
 * Returns a debounced version of the provided function.
 * The debounced function delays invoking the original function
 * until after the specified delay has elapsed since the last time it was called.
 *
 * @param {(...args: any[]) => void} fn - The function to debounce.
 * @param {number} [delay=500] - The delay in milliseconds.
 * @returns {(...args: any[]) => void} A debounced version of the input function.
 */
export const debounce = (fn: (...args: any[]) => void, delay: number = 500) => {
  let timer: NodeJS.Timeout;

  return (...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

/**
 *  Mapper function for record details - this maps the record details from API model type to UI model.
 *
 * @param data records array
 * @returns format record details array with UI data model
 */
// eslint-disable-next-line complexity
export const formatRecordDetailsData = (data: any) => {
  // eslint-disable-next-line complexity
  return data["narrative-data"]?.map((item: any) => ({
    sourceId: item.source_system,
    recordId: item.ts_id,
    reviewType: item.review_types || "-",
    // Prefer the source date; keep the tracker date as the historical fallback.
    modified: item.source_modified_at || item.last_modified_date,
    extracted: item.created_dt,
    region: item.ts_region,
    country: item.country,
    product: item.product || "-",
    ocepLink: item.ocep_link,
    risk: item.risk_types || "-",
    hcSeverity: item.hc_severity,
    aePcSsSeverity: item.ae_pc_ss_severity,
    hcReason: item.hc_reason,
    aePcSsReason: item.ae_pc_ss_reason,
    adverseEventNumbers: item.adverse_event_numbers,
    productComplaintNumbers: item.product_complaint_numbers,

    status: item.status || "-",
    affiliates: item.affiliates,
    autoReviewed: item.auto_reviewed,

    sampleId: item.sample_id,
    isSampled: item.sample,
    sampled: item.sample_created_on,
    sampleReviewType: item.sample_review_type,
    ta: item.therapeutic_area,
    version: item.version_id,

    latest: item.latest === "Yes",

    rating: item.translation_score !== "" ? item.translation_score : null,
    recordTextId: item.id,
    freeText: {
      creatorEmail: item.creator_email,
      creatorEmails: item.creator_emails || [],
      responsibleEmails: item.responsible_emails || item.creator_emails || [],
      responsiblePeople: item.responsible_people?.map((person: any) => ({
        email: person.email,
        // Every Salesforce title carries the same redundant source prefix.
        jobTitle: person.job_title?.replace(/^OCE-P - /, ""),
      })),
      ocepIds: item.ocep_ids || [],
      created: item.record_submitted_date || item.created_dt || "-",
      originalContent: {
        header: item.field_name,
        subHeader: item.field_value,
        comments: [],
        language: item.source_language,
      },
      translatedContent: {
        header: item.translated_name,
        subHeader: item.translated_text,
        comments: [],
        language: item.target_language,
        note: item.translation_message,
      },
    },

    reviewHistory: data["evaluation-data"]
      ? data?.["evaluation-data"]?.map((review: any) => ({
          id: review.ts_id,
          version: review.narrative_version_id,
          reviewId: review.evaluation_id,
          status: review.status,
          riskType: review.risk_type?.join(", "),
          reviewer: review.reviewer_email_id,
          reviewDate: review.evaluated_on,
          comment: review.corrective_action,
        }))
      : null,
  }));
};

/**
 * Filters a list of record data by a search string.
 * The search is case-insensitive and matches against multiple fields including:
 * recordId, risk, reviewType, region, country, status, product, and the formatted extracted date.
 *
 * @param {RecordDataType[]} data - The array of record data to filter.
 * @param {string} search - The search term to filter records by.
 * @returns {RecordDataType[]} The filtered array of records matching the search term.
 */

const normaliseSearchValue = (value: unknown): string =>
  (value ?? "").toString().toLowerCase();

const searchValuesFrom = (value: unknown): string[] => {
  const values = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  return values
    .map((item) => normaliseSearchValue(item).trim())
    .filter(Boolean);
};

const recordSearchValues = (item: RecordDataType): string[] => [
  normaliseSearchValue(item?.recordId),
  ...searchValuesFrom(item?.risk),
  ...searchValuesFrom(item?.reviewType),
  normaliseSearchValue(item?.region),
  normaliseSearchValue(item?.country),
  normaliseSearchValue(item?.status),
  normaliseSearchValue(item?.product),
  item?.extracted ? normaliseSearchValue(getFormattedDate(item.extracted)) : "",
];

const recordMatchesSearch = (item: RecordDataType, term: string): boolean =>
  recordSearchValues(item).some((value) => value.includes(term));

export const searchRecordData = (
  data: RecordDataType[] = [],
  search = "",
): RecordDataType[] => {
  const query = (search ?? "").trim();
  if (!query) return data;
  const terms = query
    .toLowerCase()
    .split(",")
    .map((term) => term.trim())
    .filter(Boolean);
  return data.filter((item) =>
    terms.some((term) => recordMatchesSearch(item, term)),
  );
};

/**
 * Filters a list of audit log data by a search string.
 * The search is case-insensitive and matches against multiple fields including:
 * audit id,recordId, risk, reviewType, region, country, status, product, and the formatted extracted date.
 *
 * @param {AuditLogsDataType[]} data - The array of record data to filter.
 * @param {string} search - The search term to filter records by.
 * @returns {AuditLogsDataType[]} The filtered array of records matching the search term.
 */

const normaliseAuditValue = (field: unknown): string =>
  field == null ? "" : String(field).toLowerCase();

const normaliseAuditDate = (field: string | Date): string =>
  field ? normaliseAuditValue(getFormattedCreateDate(field)) : "";

const auditLogSearchFields = (item: AuditLogsDataType): string[] => [
  normaliseAuditValue(item.audit_id),
  normaliseAuditValue(item.ts_id),
  normaliseAuditValue(item.id),
  normaliseAuditValue(item.user_id),
  normaliseAuditValue(item.user_email_id),
  normaliseAuditValue(item.user_role),
  normaliseAuditDate(item.created_dt),
  normaliseAuditValue(item.text),
  normaliseAuditDate(item.date_time_of_entry),
  normaliseAuditValue(item.action),
  normaliseAuditValue(item.corrective_action_details),
  normaliseAuditValue(item.evaluation),
  normaliseAuditValue(item.review_type),
  normaliseAuditValue(item.notification_status),
  normaliseAuditValue(item.notification_timestamp),
  normaliseAuditValue(item.notification_recipient),
  normaliseAuditValue(item.country),
  normaliseAuditValue(item.region),
  normaliseAuditValue(item.sample_id),
  normaliseAuditValue(item.version_id),
  normaliseAuditValue(item.evaluated_risk),
];

export const searchAuditLogsData = (
  data: AuditLogsDataType[] = [],
  search = "",
): AuditLogsDataType[] => {
  const query = (search ?? "").trim().toLowerCase();
  if (!query) return data;
  return data.filter((item) =>
    auditLogSearchFields(item).some((field) => field.includes(query)),
  );
};

export const formatValueForStringArrayToString = (value: any): string => {
  let finalValue: string = "";
  if (!value) {
    value = "None";
  } else {
    const substring = "[";
    if (value.includes(substring) === true) {
      let convertToArray: any = [];
      convertToArray = value.replace(/'/g, '"'); //replacing all ' with "
      convertToArray = JSON.parse(convertToArray);
      console.log(convertToArray.toString());
      finalValue = convertToArray.toString();
    } else {
      finalValue = value;
    }
  }
  return finalValue;
};

/**
 * Formats a value for CSV export.
 *
 * - Arrays are joined with a comma.
 * - Null or undefined values are replaced with "-".
 * - Strings with special characters are escaped and quoted.
 * - Special handling for `pass_count` or `fail_count` fields using `getRiskCountSummary`.
 *
 * @param {any} value - The raw value from the data row.
 * @param {string} field - The column field name.
 * @param {any} row - The entire row object for context.
 * @returns {string} - A properly formatted CSV value.
 */
// eslint-disable-next-line complexity
export const formatValueForCSV = (
  value: any,
  field: string,
  row: any,
): string => {
  const riskTypes = row?.qc_queue_sampling || "";
  if (field === "hc_severity") {
    return /HC/.test(riskTypes) ? value || "-" : "-";
  }
  if (field === "ae_pc_ss_severity") {
    if (!/AE|PC|SS/.test(riskTypes)) return "-";
    return row?.reported ? "Reported" : value || "-";
  }
  if (!value) {
    value = "-";
  } else if (field === "evaluation_risk_type") {
    value = `${value.join("/")}-${row.qc_status ?? ""}`;
  } else if (Array.isArray(value)) {
    value = value.join(", ");
  }

  if (typeof value === "string") {
    value = value.replace(/\n/g, " ");
    if (/[",]/.test(value)) {
      value = `${value.replace(/"/g, '""')}`;
    }
  }

  return value;
};

/**
 * Formats a value for CSV export.
 *
 * - Arrays are joined with a comma.
 * - Null or undefined values are replaced with "-".
 * - Strings with special characters are escaped and quoted.
 *
 * @param {any} value - The raw value from the data row.
 * @returns {string} - A properly formatted CSV value.
 */
export const formatValueForCSVAuditLog = (value: any): string => {
  if (!value) {
    value = "-";
  } else if (Array.isArray(value)) {
    value = value.join(", ");
  }

  if (typeof value === "string") {
    value = value.replace(/\n/g, " ");
    if (/[",]/.test(value)) {
      value = `${value.replace(/"/g, '""')}`;
    }
  }

  return value;
};

/**
 * Generates a timestamped export file name with a given extension.
 *
 * @param {string} extension - The file extension (e.g., 'csv', 'pdf').
 * @returns {string} - The formatted export file name (e.g., export_20250322_141522.csv).
 */
export const generateExportFileName = (extension: string): string => {
  const now = new Date();
  const datePart = now.toISOString().slice(0, 10).replace(/-/g, "");
  const timePart = now.toISOString().slice(11, 19).replace(/:/g, "");
  return `export_${datePart}_${timePart}.${extension}`;
};

/**
 * Converts data rows into an array format suitable for jsPDF autoTable.
 *
 * @param {any[]} data - The raw export data.
 * @param {string[]} selectedHeaders - Keys to include in the export.
 * @returns {any[][]} - Formatted table rows for PDF.
 */
export const formatRowsForPDF = (
  data: any[],
  selectedHeaders: string[],
): any[][] => {
  return data.map((row) =>
    selectedHeaders.map((header: string) => {
      const fieldName = requiredColumns.find(
        (item) => item.headerName === header,
      )?.field;
      return formatValueForCSV(row[fieldName!], fieldName!, row);
    }),
  );
};

/**
 * Converts data rows into an array format suitable for jsPDF autoTable.
 *
 * @param {any[]} data - The raw export data.
 * @param {string[]} selectedHeaders - Keys to include in the export.
 * @returns {any[][]} - Formatted table rows for PDF.
 */
export const formatRowsForAuditLogPDF = (
  data: any[],
  selectedHeaders: string[],
): any[][] => {
  return data.map((row) =>
    selectedHeaders.map((header: string) => {
      const fieldName = requiredAuditLogColumns.find(
        (item) => item.headerName === header,
      )?.field;
      let value = row[fieldName!] || "";
      if (!value) {
        value = "-";
      } else if (Array.isArray(value)) {
        value = value.join(", ");
      }
      return value;
    }),
  );
};

export function sanitizeText(input: string) {
  if (!input) return "";
  const escaped = input.replace(/<[^>]*>?/gm, "");
  return escaped;
}

export const getEndpoint = (needsReview: boolean, noRisk: boolean): string => {
  if (needsReview) return ENDPOINT.GET_RECORD_LIST_BY_NEEDS_REVIEW;
  if (noRisk) return ENDPOINT.GET_NO_RISK_DATA;
  return ENDPOINT.GET_REVIEWED_LIST;
};

export const isFilterApplied = (selectedFilter: any): boolean =>
  selectedFilter?.reviewType?.length > 0 &&
  selectedFilter?.startDate?.trim() !== "" &&
  selectedFilter?.endDate?.trim() !== "";

export const isValidFilter = (
  selectedFilter: any,
  endpoint: string,
  dataLimit: number | null,
): boolean => {
  return (
    isFilterApplied(selectedFilter) && endpoint !== "" && dataLimit !== null
  );
};

// eslint-disable-next-line complexity
export const buildFormBody = (
  selectedFilter: any,
  dataLimit: number | null,
  countryGroupMembers: Record<string, string[]> = {},
): string => {
  const body = {
    reviewType: selectedFilter?.reviewType,
    selectedRegions: selectedFilter?.selectedRegions,
    startDate: selectedFilter?.startDate,
    endDate: selectedFilter?.endDate,
    limit: dataLimit,
    selectedCountries: expandCountryFilters(
      selectedFilter?.selectedCountries,
      countryGroupMembers,
    ),
    selectedAffiliates: expandCountryFilters(
      selectedFilter?.selectedAffiliates,
      countryGroupMembers,
    ),
    selectedEvaluation: selectedFilter?.selectedEvaluation,
    selectedRisk: selectedFilter?.selectedRisk,
    selectedProduct: selectedFilter?.selectedProduct,
    selectedSampleId: selectedFilter?.selectedSampleId,
    selectedStatus: selectedFilter?.selectedStatus,
    selectedFieldName: expandFieldLabelFilters(
      selectedFilter?.selectedFieldName,
    ),
  };

  return JSON.stringify(body);
};

/**
 * Builds the JSON body for the search-records lambda.
 * Search is status-independent — the backend searches all statuses at once.
 */
// eslint-disable-next-line complexity
export const buildSearchBody = (
  selectedFilter: any,
  searchTerm: string,
  dataLimit: number[] | null,
  countryGroupMembers: Record<string, string[]> = {},
): string => {
  const body = {
    reviewType: selectedFilter?.reviewType,
    selectedRegions: selectedFilter?.selectedRegions,
    startDate: selectedFilter?.startDate,
    endDate: selectedFilter?.endDate,
    limit: dataLimit,
    selectedCountries: expandCountryFilters(
      selectedFilter?.selectedCountries,
      countryGroupMembers,
    ),
    selectedAffiliates: expandCountryFilters(
      selectedFilter?.selectedAffiliates,
      countryGroupMembers,
    ),
    selectedEvaluation: selectedFilter?.selectedEvaluation,
    selectedRisk: selectedFilter?.selectedRisk,
    selectedProduct: selectedFilter?.selectedProduct,
    selectedSampleId: selectedFilter?.selectedSampleId,
    selectedStatus: selectedFilter?.selectedStatus,
    selectedFieldName: expandFieldLabelFilters(
      selectedFilter?.selectedFieldName,
    ),
    searchTerm,
  };
  return JSON.stringify(body);
};

/** Maps record status string to UI tab index */
export const STATUS_TO_TAB: Record<string, number> = {
  "Pending Review": 0,
  Reviewed: 1,
  "Available for Review": 2,
};

export const blockInvalidChar = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
};

export const hasNonZeroData = (value: unknown): boolean => {
  if (typeof value === "number") return value > 0;
  if (Array.isArray(value)) return value.some(hasNonZeroData);
  if (value && typeof value === "object") {
    return Object.values(value).some(hasNonZeroData);
  }
  return false;
};

const escapeCsv = (v: any): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

async function* csvChunks<T>(
  requiredColumns: RequiredColumn<T>[],
  data: [],
  formatValueForCSV: FormatValueForCSV<T>,
): AsyncGenerator<string, void, void> {
  yield requiredColumns.map((c) => escapeCsv(c.headerName)).join(",") + "\r\n";

  for (const row of data) {
    const line = requiredColumns
      .map((c) => escapeCsv(formatValueForCSV(row[c.field], c.field, row)))
      .join(",");
    yield line + "\r\n";
  }
}

export async function exportCsvStream<T>(
  fileName: string,
  requiredColumns: RequiredColumn<T>[],
  data: [],
  formatValueForCSV: FormatValueForCSV<T>,
): Promise<void> {
  const suggestedName = fileName.toLowerCase().endsWith(".csv")
    ? fileName
    : `${fileName}.csv`;
  const parts: BlobPart[] = [];
  for await (const chunk of csvChunks(
    requiredColumns,
    data,
    formatValueForCSV,
  )) {
    parts.push(chunk);
  }
  const blob = new Blob(parts, { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
